import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useApi } from '../lib/api';

const ROLE_LABELS = { admin: 'Admin', member: 'Member' };

export default function AcceptInvite() {
  const { token } = useParams();
  const { isSignedIn, isLoaded } = useAuth();
  const api = useApi();
  const navigate = useNavigate();

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.getInvitationByToken(token)
      .then(data => setInvite(data))
      .catch(err => setError(err.response?.data?.error || 'Invitation not found'))
      .finally(() => setLoading(false));
  }, [token]); // eslint-disable-line

  // Skip the confirmation card entirely for cases that don't need a decision:
  // no account yet → straight to signup (it'll pick the pending invite up
  // automatically once signed in); already-resolved invite (accepted/declined
  // on a previous visit to this same email link) → straight to the dashboard
  // instead of a redundant "already accepted" screen with its own button.
  useEffect(() => {
    if (loading || error || !invite || !isLoaded) return;
    if (!isSignedIn) {
      navigate('/signup', { replace: true });
    } else if (invite.status !== 'pending') {
      navigate('/', { replace: true });
    }
  }, [loading, error, invite, isLoaded, isSignedIn]); // eslint-disable-line

  const handleAccept = async () => {
    setBusy('accept');
    try {
      await api.acceptInvitation(token);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
      setBusy(null);
    }
  };

  const handleDecline = async () => {
    setBusy('decline');
    try {
      await api.declineInvitation(token);
      setDone('declined');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to decline invitation');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src="/logo.png" alt="Glos" style={styles.logo} />

        {loading && <p style={styles.muted}>Loading invitation…</p>}

        {!loading && error && (
          <>
            <div style={styles.icon}>❌</div>
            <h2 style={styles.title}>Invitation not found</h2>
            <p style={styles.sub}>{error}</p>
            <button style={styles.btnPrimary} onClick={() => navigate('/')}>Go to Glos</button>
          </>
        )}

        {!loading && !error && invite && !done && (
          !isLoaded || !isSignedIn || invite.status !== 'pending' ? (
            <p style={styles.muted}>Redirecting…</p>
          ) : (
            <>
              <div style={styles.icon}>🎉</div>
              <h2 style={styles.title}>You've been invited!</h2>
              <p style={styles.sub}>
                <strong>{invite.invited_by}</strong> has invited you to join{' '}
                <strong>{invite.app_name}</strong> as a{' '}
                <span style={styles.roleBadge}>{ROLE_LABELS[invite.role] || invite.role}</span>.
              </p>
              <div style={styles.actions}>
                <button
                  style={styles.btnPrimary}
                  onClick={handleAccept}
                  disabled={!!busy}
                >
                  {busy === 'accept' ? 'Accepting…' : 'Accept Invitation'}
                </button>
                <button
                  style={styles.btnSecondary}
                  onClick={handleDecline}
                  disabled={!!busy}
                >
                  {busy === 'decline' ? 'Declining…' : 'Decline'}
                </button>
              </div>
            </>
          )
        )}

        {done === 'declined' && (
          <>
            <div style={styles.icon}>👋</div>
            <h2 style={styles.title}>Invitation declined</h2>
            <p style={styles.sub}>You've declined the invitation to <strong>{invite?.app_name}</strong>.</p>
            <button style={styles.btnSecondary} onClick={() => navigate('/')}>Go to Glos</button>
          </>
        )}
      </div>
    </div>
  );
}

// Mirrors the invitation email's card (baseHtmlV2 in resendMail.js) so the
// in-app landing page after clicking "Accept Invitation" feels like a
// continuation of the email rather than a different, plainer product.
const styles = {
  page: {
    minHeight: '100vh',
    background: '#eef1f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 16px',
    fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif",
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    padding: '40px 36px',
    maxWidth: 440,
    width: '100%',
    boxShadow: '0 1px 3px rgba(20,20,50,0.06), 0 12px 32px rgba(20,20,50,0.08)',
    textAlign: 'center',
  },
  logo: {
    height: 40,
    width: 'auto',
    marginBottom: 26,
  },
  icon: {
    fontSize: 44,
    marginBottom: 14,
  },
  title: {
    margin: '0 0 10px',
    fontSize: 24,
    fontWeight: 800,
    color: '#152045',
    letterSpacing: '-0.01em',
  },
  sub: {
    color: '#374151',
    fontSize: 14.5,
    lineHeight: 1.65,
    marginBottom: 26,
  },
  muted: {
    color: '#6b7280',
    fontSize: 14,
    lineHeight: 1.6,
  },
  roleBadge: {
    background: '#6c63ff',
    color: '#fff',
    borderRadius: 4,
    padding: '2px 10px',
    fontSize: 12.5,
    fontWeight: 700,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  btnPrimary: {
    display: 'block',
    width: '100%',
    background: '#2563eb',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '13px 0',
    fontSize: 14.5,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
  btnSecondary: {
    display: 'block',
    width: '100%',
    background: '#f6f7fb',
    color: '#374151',
    border: '1px solid #ececf4',
    borderRadius: 8,
    padding: '13px 0',
    fontSize: 14.5,
    fontWeight: 700,
    cursor: 'pointer',
    textDecoration: 'none',
    textAlign: 'center',
    boxSizing: 'border-box',
  },
};
